import { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Settings, FileStack } from 'lucide-react';
import InventorySubNav from './InventorySubNav';
import PropertiesListView, { PropertiesListViewRef } from './views/PropertiesListView';
import ItemsCatalogView, { ItemsCatalogViewRef } from './views/ItemsCatalogView';
import PropertyArticlesView from './views/PropertyArticlesView';
import TemplatesModal from './modals/TemplatesModal';

export type InventoryView = 'properties' | 'items' | 'property-detail';

export interface InventoryTabRef {
  openCreateModal: () => void;
}

type SubTab = 'properties' | 'items';

const InventoryTab = forwardRef<InventoryTabRef>((_, ref) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('properties');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [showPropertyDetail, setShowPropertyDetail] = useState(false);
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);

  const propertiesListRef = useRef<PropertiesListViewRef>(null);
  const itemsCatalogRef = useRef<ItemsCatalogViewRef>(null);

  useImperativeHandle(ref, () => ({
    openCreateModal: () => {
      if (activeSubTab === 'items') {
        itemsCatalogRef.current?.openCreateModal();
      }
    },
  }));

  const handleViewArticles = (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    setShowPropertyDetail(true);
  };

  const handleBackToList = () => {
    setSelectedPropertyId(null);
    setShowPropertyDetail(false);
  };

  const handleCreate = () => {
    if (activeSubTab === 'items') {
      itemsCatalogRef.current?.openCreateModal();
    }
  };

  const handleOpenCategories = () => {
    itemsCatalogRef.current?.openCategoriesModal();
  };

  return (
    <div className="space-y-6">
      {!showPropertyDetail && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between min-h-10">
            <h2 className="text-2xl font-bold">Inventario</h2>
            
            <div className="flex items-center gap-2">
              {activeSubTab === 'properties' && (
                <Button variant="outline" onClick={() => setIsTemplatesModalOpen(true)}>
                  <FileStack size={16} className="mr-2" />
                  Plantillas
                </Button>
              )}
              {activeSubTab === 'items' && (
                <>
                  <Button variant="outline" onClick={handleOpenCategories}>
                    <Settings size={16} className="mr-2" />
                    Categorías
                  </Button>
                  <Button 
                    onClick={handleCreate}
                    className="bg-brand-primary hover:bg-brand-primary-hover"
                  >
                    <Plus size={16} className="mr-2" />
                    Nuevo Artículo
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* SubNav */}
          <InventorySubNav 
            activeSubTab={activeSubTab} 
            onSubTabChange={setActiveSubTab} 
          />
        </div>
      )}

      {/* Content */}
      {showPropertyDetail && selectedPropertyId ? (
        <PropertyArticlesView propertyId={selectedPropertyId} onBack={handleBackToList} />
      ) : activeSubTab === 'properties' ? (
        <PropertiesListView ref={propertiesListRef} onViewArticles={handleViewArticles} />
      ) : (
        <ItemsCatalogView ref={itemsCatalogRef} />
      )}

      {/* Templates Modal */}
      <TemplatesModal 
        open={isTemplatesModalOpen} 
        onOpenChange={setIsTemplatesModalOpen} 
      />
    </div>
  );
});

InventoryTab.displayName = 'InventoryTab';

export default InventoryTab;
